<template>
  <v-dialog
      :value="value"
      fullscreen
      hide-overlay
      persistent
      transition="dialog-bottom-transition"
  >
    <v-card height="100%">
      <div class="float-right" id="close-btn">
        <v-btn fab color="rgba(0, 0, 0, 0.8)" @click="close">
          <v-icon color="rgb(255, 255, 255)">mdi-close</v-icon>
        </v-btn>
      </div>
      <div ref="pano" id="pano"></div>
      <div class="minimap" ref="minimap_container">
        <div ref="minimap" style="width: 100%; height: 100%;"></div>
        <v-btn v-on:click="zoomOut" id="zoom-btn">
          <v-icon v-if="!zoomedOut">mdi-fullscreen</v-icon>
          <v-icon v-if="zoomedOut">mdi-fullscreen-exit</v-icon>
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import Marzipano from 'marzipano';
import {Map} from "ol";
import LayerGroup from "ol/layer/Group";
import Feature from "ol/Feature";
import {Icon, Style} from "ol/style";
import {store} from "@/store";
export default {
  name: "Image360Dialog",
  props: ['value', 'image360', 'map'],
  data() {
    return {
      miniMap: null,
      zoomedOut: false,
      scene: null,
      colorLayer: null,
      thermalLayer: null,
      satelliteLayer: null,
      colorLayerStatus: {
        visible: null,
        opacity: null,
        zIndex: null,
      },
      thermalLayerStatus: {
        visible: null,
        opacity: null,
        zIndex: null,
      },
      satelliteLayerStatus: {
        visible: null,
        opacity: null,
      },
      mapViewStatus: {
        center: null,
        zoom: null,
      }
    }
  },

  methods: {
    initImage360() {
      const viewer = new Marzipano.Viewer(this.$refs.pano);
      const source = Marzipano.ImageUrlSource.fromString(this.image360.feature.get('url'));
      const geometry = new Marzipano.EquirectGeometry([{ width: this.image360.feature.get('width') }]);
      const limiter = Marzipano.RectilinearView.limit.traditional(this.image360.feature.get('width')/4, 90*Math.PI/180);
      const view = new Marzipano.RectilinearView({ yaw: Math.PI }, limiter);

      this.scene = viewer.createScene({
        source: source,
        geometry: geometry,
        view: view,
        pinFirstLevel: true
      });

      this.scene.switchTo();
    },
    close(){
      store.updateOpenDialog(false);
      this.$emit('input');
      this.image360.image360Layer.getSource().removeFeature(this.image360.markerFeature);
      this.satelliteLayer.setVisible(this.satelliteLayerStatus.visible);
      this.satelliteLayer.setOpacity(this.satelliteLayerStatus.opacity);
      this.restoreLayer(this.colorLayer, this.colorLayerStatus);
      this.restoreLayer(this.thermalLayer, this.thermalLayerStatus);
      this.map.getView().setCenter(this.mapViewStatus.center);
      this.map.getView().setZoom(this.mapViewStatus.zoom);
      this.image360.markerFeature = null;
      this.image360.image360Id = null;
      this.image360.feature = null;
      this.image360.image360Layer = null;
    },
    zoomOut(){
      this.zoomedOut = !this.zoomedOut;
    },
    restoreLayer(layer, layerStatus){
      layer.setVisible(layerStatus.visible);
      layer.setOpacity(layerStatus.opacity);
      layer.setZIndex(layerStatus.zIndex);
    },
    updateLayerStatus(layerStatus, layer){
      layerStatus.visible = layer.getVisible();
      layerStatus.opacity = layer.getOpacity();
      layerStatus.zIndex = layer.getZIndex();
    },
    initOrthoLayer(layer, type) {
      layer.setVisible(true);
      layer.setOpacity(1);
      switch (type){
        case 'color':{
          layer.setZIndex(3);
          break;
        }
        case 'thermal':{
          layer.setZIndex(2);
          break;
        }
      }
    },
    initMiniMap(){
      this.colorLayer = this.getLayerByTitle('可視オルソ画像', this.map);
      this.updateLayerStatus(this.colorLayerStatus, this.colorLayer);
      this.initOrthoLayer(this.colorLayer, 'color');

      this.thermalLayer = this.getLayerByTitle('サーモオルソ画像', this.map);
      this.updateLayerStatus(this.thermalLayerStatus, this.thermalLayer);
      this.initOrthoLayer(this.thermalLayer, 'thermal');

      this.satelliteLayer = this.getLayerByTitle('Satellite', this.map);
      this.satelliteLayer.getSource().setAttributions('');
      this.satelliteLayerStatus.visible = this.satelliteLayer.getVisible();
      this.satelliteLayerStatus.opacity = this.satelliteLayer.getOpacity();
      this.satelliteLayer.setVisible(true);
      this.satelliteLayer.setOpacity(1);

      this.initMapMarker(this.image360.feature);

      const view = this.map.getView();
      this.mapViewStatus.center = view.getCenter();
      this.mapViewStatus.zoom = view.getZoom();
      view.setCenter(this.image360.feature.getGeometry().getCoordinates());
      view.setZoom(17);

      this.miniMap = new Map({
        target: this.$refs.minimap,
        layers: [
            this.image360.image360Layer,
            this.colorLayer,
            this.thermalLayer,
            this.satelliteLayer,
        ],
        view: view
      });
      this.miniMap.on('click', (evt) => {
        const pixel = this.miniMap.getEventPixel(evt.originalEvent);
        const hit = this.miniMap.hasFeatureAtPixel(pixel);
        if (hit){
          const feature = this.miniMap.forEachFeatureAtPixel(pixel, (feature) => {
            return feature;
          }, {
            layerFilter: (layer) => {
              if (layer.get('image360Id')){
                return true;
              }
              return false;
            }
          });
          if (feature){
            this.image360.feature = feature;
            this.initImage360();
            this.initMapMarker();
          }
        }
      });
    },
    getLayerByTitle(title, layer){
      const array = layer.getLayers().getArray();
      for (const layer of array){
        if (layer.get('title') === title){
          return layer;
        }
        else if (layer instanceof LayerGroup){
          const a = this.getLayerByTitle(title, layer);
          if (a) return a;
        }
      }
      return null;
    },
    initMapMarker(){
      if (this.image360.markerFeature){
        this.image360.image360Layer.getSource().removeFeature(this.image360.markerFeature);
      }

      this.image360.markerFeature = new Feature({
        geometry: this.image360.feature.getGeometry(),
      });

      this.image360.markerFeature.setStyle(new Style({
        image: new Icon({
          anchor: [0.5, 1],
          anchorXUnits: 'fraction',
          anchorYUnits: 'fraction',
          src: require('@/assets/images/map-marker.png'),
        })
      }));

      this.image360.image360Layer.getSource().addFeature(this.image360.markerFeature);
    }
  },

  watch:{
    zoomedOut(zoomedOut){
      if (zoomedOut){
        this.$refs.minimap_container.classList.add('zoom-out');
        if (this.$refs.minimap_container.classList.contains('zoom-in')){
          this.$refs.minimap_container.classList.remove('zoom-in');
        }
      }
      else {
        this.$refs.minimap_container.classList.add('zoom-in');
        if (this.$refs.minimap_container.classList.contains('zoom-out')){
          this.$refs.minimap_container.classList.remove('zoom-out');
        }
      }
    }
  },

  mounted() {
    store.updateOpenDialog(true);
    this.initImage360();
    this.initMiniMap();
    this.$refs.minimap_container.addEventListener('animationend', () => {
      this.miniMap.updateSize();
    }, false);
  },
}
</script>

<style scoped>
#pano{
  height: 100%;
}
#close-btn{
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 1;
}
.minimap{
  position: absolute;
  top: 0;
  left: 0;
  width: 350px;
  height: 200px;
}

@keyframes zoom-out {
  from {
    width: 350px;
    height: 200px;
  }
  to {
    width: 700px;
    height: 400px;
  }
}

.zoom-out{
  width: 700px;
  height: 400px;
  animation: zoom-out;
  animation-duration: 0.1s;
  animation-delay: 0s;
}

@keyframes zoom-in {
  from {
    width: 700px;
    height: 400px;
  }
  to {
    width: 350px;
    height: 200px;
  }
}

.zoom-in{
  width: 350px;
  height: 200px;
  animation: zoom-in;
  animation-duration: 0.1s;
  animation-delay: 0s;
}

#zoom-btn{
  position: absolute;
  bottom: 10px;
  right: 10px;
}
</style>
